import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Button — canonical button for Agentic Loan Audit.
 *
 * Variants (flat Material-3):
 *   primary   — filled solid primary (burgundy). Primary affirmative action.
 *   secondary — tonal: secondary-container surface. Neutral affirmative.
 *   ghost     — text button, no background until hover. Tertiary/toolbar.
 *   danger    — filled error. Destructive confirmations.
 *
 * Sizes: sm (h-8), md (h-10 default), lg (h-11, matches iOS 44px rule).
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:
    'text-on-primary bg-primary hover:bg-primary-container',
  secondary:
    'bg-secondary-container text-primary hover:bg-outline-variant/40',
  ghost:
    'bg-transparent text-on-surface-variant hover:bg-surface-variant/60',
  danger:
    'text-on-error bg-error hover:bg-error/90',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-caption rounded-lg',
  md: 'h-10 px-4 text-label rounded-lg',
  lg: 'h-11 px-5 text-body rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', leftIcon, rightIcon, loading = false, className, children, disabled, type, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        // Default to type="button" so buttons inside forms don't accidentally
        // submit. Callers that need submit/reset still pass an explicit type.
        type={type ?? 'button'}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200',
          'disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        {...rest}
      >
        {loading && (
          <span
            className="inline-block h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin"
            aria-hidden
          />
        )}
        {!loading && leftIcon}
        {children}
        {!loading && rightIcon}
      </button>
    );
  },
);
Button.displayName = 'Button';
